import Phaser from 'phaser';
import { DESIGN_WIDTH } from '../PhaserGame.js';
import { navigateBack } from '../sceneNav.js';
import { Button } from './Button.js';

// Shared page-header chrome. Mounts the BattleShark logo flush at the
// top of the canvas (full design width) with an optional Back button
// overlaid at the top-right corner. Replaced the per-scene 88px
// "header band + Back at top-left + logo below" pattern that wasted
// vertical real estate across every tabbed scene.
//
// Both the logo and the Back button mount to the SCENE ROOT (not a
// scrollable container) so they stay pinned while the scene's
// content scrolls underneath.
//
// Returns the y-coordinate just below the rendered logo so the caller
// can place its content / section tabs flush below.

const BACK_BUTTON_WIDTH = 120;
const BACK_BUTTON_HEIGHT = 42;
// Inset from the top-right corner of the logo. Right inset matches
// the GameScene action-panel YOU chip (right edge at x=705, inset
// 15) so the two right-aligned UI elements share a visual gutter
// down the right side of the canvas. The top inset keeps the
// button up near the canvas edge so it doesn't sit on the
// wordmark text.
const BACK_INSET_RIGHT = 15;
const BACK_INSET_TOP = 8;
// Depth above the logo so the Back button overlays cleanly.
const LOGO_DEPTH = 50;
const BACK_DEPTH = 60;

export interface PageHeaderOpts {
  readonly scene: Phaser.Scene;
  /** Preloaded image cache key (each scene loads under its own key
   *  so Phaser doesn't collide). */
  readonly logoAssetKey: string;
  /** Render the overlay Back button. Pass false on scenes where
   *  there's no parent to return to (e.g. Home). */
  readonly showBack: boolean;
  /** Optional override for the Back tap handler. Defaults to
   *  navigateBack(scene). Pass a custom callback when the scene
   *  needs a confirm modal (e.g. GameScene in single-player
   *  in-progress) or a hard navigation. */
  readonly onBack?: () => void;
  /** Pixels of empty space to reserve above the logo. Used by
   *  GameScene to honor LAYOUT.topSafe (iOS notch / browser status
   *  bar reservation). Tabbed scenes leave this at 0 since they
   *  don't reserve safe area inside the canvas. */
  readonly topInset?: number;
}

export interface PageHeaderResult {
  /** y-coordinate of the bottom edge of the rendered logo, in
   *  scene-root coords. Callers place their scrollable content /
   *  section tabs / page body flush below this. */
  readonly contentTop: number;
}

export function mountPageHeader(opts: PageHeaderOpts): PageHeaderResult {
  const { scene, logoAssetKey, showBack, onBack } = opts;
  const topInset = opts.topInset ?? 0;

  // Logo — top-anchored at topInset (0 for tabbed scenes, topSafe
  // for GameScene), scaled to full design width.
  const logo = scene.add.image(DESIGN_WIDTH / 2, topInset, logoAssetKey);
  const scale = DESIGN_WIDTH / logo.width;
  logo.setScale(scale);
  logo.setOrigin(0.5, 0);
  logo.setDepth(LOGO_DEPTH);
  const logoBottomY = topInset + logo.height * scale;

  if (showBack) {
    // Top-right overlay. Button uses center-origin so we offset by
    // half-width/half-height from the inset corner. Vertical inset
    // is measured from the top of the LOGO (not the canvas) so the
    // button stays anchored to the logo's corner even when topInset
    // pushes the whole header down.
    const back = new Button(scene, {
      x: DESIGN_WIDTH - BACK_INSET_RIGHT - BACK_BUTTON_WIDTH / 2,
      y: topInset + BACK_INSET_TOP + BACK_BUTTON_HEIGHT / 2,
      width: BACK_BUTTON_WIDTH,
      height: BACK_BUTTON_HEIGHT,
      text: '< Back',
      onTap: onBack ?? (() => navigateBack(scene)),
    });
    back.setDepth(BACK_DEPTH);
  }

  return { contentTop: logoBottomY };
}
